import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { VerticalCard } from '../../partials/cards/verticalCard/VerticalCard';
import { useTheme } from '../../../theming/useTheme';
import { CardComponentDataProps } from '../../partials/cards/dividedCard/DividedCard';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { BaseUnit } from '../../../theming/baseUnitDefinition';
import { LARGE_SCREEN_SIZE, MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';

const createStyles = (baseUnit: BaseUnit) => StyleSheet.create({
  listWrapper: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: `${LARGE_SCREEN_SIZE}px`,
  },
  scrollContainer: {
    '-webkit-overflow-scrolling': 'touch',
    display: 'flex',
    justifyContent: 'space-between',
    overflowX: 'scroll',
    overflowY: 'visible',
    [MEDIA_BREAKPOINTS.LARGE]: {
      overflow: 'visible',
      justifyContent: 'center',
      gridTemplateColumns: '1fr 1fr 1fr 1fr',
      gridColumnGap: `${baseUnit / 2}px`,
      display: 'grid',
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingBottom: `${baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingBottom: `${baseUnit}px`,
    },
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  scrollItem: {
    flex: '0 0 auto',
    flexBasis: `${300 - baseUnit / 2}px`,
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `0 ${baseUnit / 4}px`,
      ':first-child': {
        marginLeft: 0,
      },
      ':last-child': {
        marginRight: 0,
      },
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `0 ${baseUnit / 4}px`,
      ':first-child': {
        marginLeft: 0,
      },
      ':last-child': {
        marginRight: 0,
      },
    },
    position: 'relative',
    boxSizing: 'border-box',
    '@media (max-width: 330px)': {
      flexBasis: '85%',
    },
  },
});

interface ScrollableArticleListComponentDataProps {
  cardsProps: CardComponentDataProps[]
}

interface ScrollableArticleListComponentThemeProps {
  baseUnit: BaseUnit
}

type ScrollableArticleListComponentProps = ScrollableArticleListComponentDataProps & ScrollableArticleListComponentThemeProps;


export const ScrollableArticleListComponent: React.FunctionComponent<ScrollableArticleListComponentProps> = ({ cardsProps, baseUnit }) => {
  const styles = createStyles(baseUnit);
  const createCard = (props: CardComponentDataProps) => {
    const { id } = props;
    return (
      <div className={css(styles.scrollItem)} key={id}>
        <VerticalCard {...props} />
      </div>
    );
  };

  return (
    <div className={css(styles.listWrapper)}>
      <div className={css(styles.scrollContainer)}>
        {cardsProps.map(createCard)}
      </div>
    </div>
  );
};

ScrollableArticleListComponent.defaultProps = {
  cardsProps: [],
};

const mapThemeProps = (theme: ScrollableArticleListComponentThemeProps) => ({
  baseUnit: theme.baseUnit,
});

export const ScrollableArticleList = (props: ScrollableArticleListComponentDataProps) => {
  const themeContext = useTheme(mapThemeProps);
  return <ErrorBoundary><ScrollableArticleListComponent {...themeContext} {...props} /></ErrorBoundary>;
};
